import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

function CodeForm({ kind, onDone }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/verify/${kind}/confirm`, { code });
      toast.success(kind === "email" ? "Email verified!" : "Phone verified!");
      onDone();
    } catch (e2) { err(e2); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={confirm} className="flex gap-2 mt-3">
      <Input data-testid={`${kind}-code-input`} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit code" className="rounded-xl bg-input border-border tracking-[0.3em] font-mono" />
      <Button data-testid={`${kind}-confirm-btn`} disabled={code.length !== 6 || busy} className="rounded-xl bg-[#6C0BA9] hover:bg-[#8012C6] text-white">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
      </Button>
    </form>
  );
}

const Done = ({ testid, text }) => (
  <div data-testid={testid} className="flex items-center gap-1.5 text-sm text-emerald-400 font-medium"><CheckCircle2 className="w-4 h-4" />{text}</div>
);

function EmailRow() {
  const { user, refresh } = useAuth();
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post("/verify/email/send");
      setSent(true);
      toast.success(`Code sent to ${user.email}`);
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <div className="p-4 rounded-xl border border-border">
      <div className="flex items-center gap-3 flex-wrap">
        <Mail className="w-5 h-5 text-[#C084FC]" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Email</div>
          <div className="text-xs text-muted-foreground truncate">{user.email}</div>
        </div>
        {user.email_verified ? <Done testid="email-verified-status" text="Verified" /> : (
          <Button data-testid="email-send-code-btn" onClick={send} disabled={busy} variant="outline" size="sm" className="rounded-xl">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : sent ? "Resend code" : "Send code"}
          </Button>
        )}
      </div>
      {!user.email_verified && sent && <CodeForm kind="email" onDone={refresh} />}
    </div>
  );
}

function PhoneRow() {
  const { user, refresh } = useAuth();
  const [phone, setPhone] = useState("");
  const [demoCode, setDemoCode] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/verify/phone/send", { phone });
      setDemoCode(data.demo_code);
    } catch (e2) { err(e2); } finally { setBusy(false); }
  };
  return (
    <div className="p-4 rounded-xl border border-border">
      <div className="flex items-center gap-3 flex-wrap">
        <Phone className="w-5 h-5 text-[#C084FC]" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Phone</div>
          <div className="text-xs text-muted-foreground">{user.phone_verified ? user.phone : "Add a number with country code"}</div>
        </div>
        {user.phone_verified && <Done testid="phone-verified-status" text="Verified" />}
      </div>
      {!user.phone_verified && (
        <>
          <form onSubmit={send} className="flex gap-2 mt-3">
            <Input data-testid="phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" placeholder="+1 555 123 4567" className="rounded-xl bg-input border-border" />
            <Button data-testid="phone-send-code-btn" disabled={!phone.trim() || busy} variant="outline" className="rounded-xl">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : demoCode ? "Resend" : "Send code"}
            </Button>
          </form>
          {demoCode && (
            <>
              <div data-testid="phone-demo-code" className="mt-3 text-xs rounded-xl px-3 py-2 bg-amber-500/10 border border-amber-500/30 text-amber-300">
                Demo mode — no SMS is sent yet. Your code is <span className="font-mono font-bold tracking-widest">{demoCode}</span>
              </div>
              <CodeForm kind="phone" onDone={refresh} />
            </>
          )}
        </>
      )}
    </div>
  );
}

export default function VerificationCard() {
  const { user } = useAuth();
  const trusted = user.email_verified || user.phone_verified;
  return (
    <section data-testid="verification-card" className="bg-card border border-border rounded-2xl p-5 space-y-3">
      <div>
        <h2 className="font-heading text-lg font-bold">Verification</h2>
        <p className="text-xs text-muted-foreground mt-1">
          {trusted ? "Your account is trusted — you can message anyone and share links." : "Verify your email or phone to message people who aren't your friends and to share links."}
        </p>
      </div>
      <EmailRow />
      <PhoneRow />
    </section>
  );
}
